import { Shop as ShopComponent } from '@shared/components/ui/shop';
import { faShoppingBag } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { defaultOrderStatusDot, statusColorMap } from '@modules/Orders/List/config/orderStatusConfig';
import { StatusBadgeWithDot } from '@modules/Orders/List/components/display/StatusBadgeWithDot';
import { Order } from '@modules/Orders/List/models/Orders';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { VirtualTable } from '@shared/components/ui/VirtualTable';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCustomerOrders } from '../../hooks/useCustomerOrders';
import { LoadingState } from '../feedback';

export interface CustomerOrdersModalRef {
  open: (customerId: number, customerName: string) => void;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

const formatCurrency = (amount: number, currency: string = 'EUR') => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

export const CustomerOrdersModal = forwardRef<CustomerOrdersModalRef>((_, ref) => {
  const { t } = useTranslation('crmModule');
  const navigate = useNavigate();
  const modalRef = useRef<ModalCenterRef>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customerName, setCustomerName] = useState<string>('');

  const { orders, isLoading, error } = useCustomerOrders(customerId ? [customerId] : undefined);

  useImperativeHandle(ref, () => ({
    open: (id: number, name: string) => {
      setCustomerId(id);
      setCustomerName(name);
      modalRef.current?.openModal();
    },
  }));

  const handleOrderClick = (order: Order) => {
    navigate(`/orders/view/${order.id}`);
    modalRef.current?.closeModal();
  };

  const columns = [
    {
      id: 'ref',
      header: t('table.order', 'Pedido'),
      accessorKey: 'ref',
      size: 20,
      cell: ({ row }: any) => (
        <button
          type="button"
          onClick={() => handleOrderClick(row.original)}
          className="text-sm font-medium text-text hover:underline text-left"
        >
          {row.original.ref}
        </button>
      ),
    },
    {
      id: 'shop',
      header: t('table.shop', 'Tienda'),
      accessorKey: 'shop',
      size: 20,
      cell: ({ row }: any) =>
        row.original.shop ? (
          <ShopComponent name={row.original.shop?.name} src={row.original.shop?.image_hash} />
        ) : (
          <span className="text-sm">-</span>
        ),
    },
    {
      id: 'status',
      header: t('table.status', 'Estado'),
      accessorKey: 'status',
      size: 20,
      cell: ({ row }: any) => {
        const code = row.original.status?.id;
        const { dot } = code ? (statusColorMap[code] ?? defaultOrderStatusDot) : defaultOrderStatusDot;
        return (
          <StatusBadgeWithDot className="text-xs" dotClassName={dot} label={row.original.status?.description || '-'} />
        );
      },
    },
    {
      id: 'order_created_at',
      header: t('table.created_at', 'Fecha'),
      accessorKey: 'order_created_at',
      size: 20,
      cell: ({ row }: any) => <span className="text-sm text-text">{formatDate(row.original.order_created_at)}</span>,
    },
    {
      id: 'total',
      header: t('table.total', 'Total'),
      accessorKey: 'total',
      size: 20,
      cell: ({ row }: any) => {
        const total =
          typeof row.original.total === 'number' ? row.original.total : parseFloat(row.original.total || '0');
        const currency = row.original.currency || 'EUR';
        return <span className="text-sm font-medium text-text">{formatCurrency(total, currency)}</span>;
      },
    },
  ];

  return (
    <ModalCenter
      ref={modalRef}
      title={
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faShoppingBag} className="w-4 h-4" />
          {customerName
            ? `${t('customerOrders.title', 'Pedidos de')} ${customerName}`
            : t('customerOrders.title', 'Pedidos del cliente')}
        </span>
      }
      type="default"
      width="lg"
      className="w-full h-full sm:w-lg sm:h-auto sm:max-w-[800px]"
    >
      <div className="space-y-4">
        {isLoading ? (
          <LoadingState message={t('loading.message', 'Cargando pedidos...')} className="h-32" />
        ) : error ? (
          <div className="text-error text-center py-8">{t('customerOrders.error', 'Error al cargar los pedidos')}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-complementary-text">
            {t('customerOrders.empty', 'No hay pedidos para este cliente')}
          </div>
        ) : (
          <VirtualTable
            columns={columns}
            data={orders}
            autoHeight={true}
            maxHeight="60vh"
            rowHeight={60}
            emptyMessage={t('customerOrders.empty', 'No hay pedidos')}
            className="rounded-lg"
          />
        )}
      </div>
    </ModalCenter>
  );
});

CustomerOrdersModal.displayName = 'CustomerOrdersModal';
